import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronLeft, ShieldCheck, Clock, Lock, Sparkles, Upload, Cog, Download } from 'lucide-react';
import * as PhosphorIcons from '@phosphor-icons/react';
import { getTool, getRelatedTools, getFAQs } from '../lib/tools';
import ToolUploader from '../components/ToolUploader';
import AdSlot from '../components/AdSlot';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useSEO, buildFAQSchema, buildHowToSchema } from '../lib/seo';

function ToolIcon({ name, color, size = 40 }) {
  const Icon = PhosphorIcons[name] || PhosphorIcons.File;
  return <Icon size={size} weight="duotone" color={color} />;
}

// ------ Per-tool option panels ------
function SplitOptions({ getRef }) {
  const [mode, setMode] = React.useState('every');
  const [ranges, setRanges] = React.useState('1-3');
  React.useImperativeHandle(getRef, () => () => ({ mode, ranges }));
  return (
    <div className="space-y-4">
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Split mode</Label>
      <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-2 gap-2">
        <label data-testid="split-mode-every" className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'every' ? 'border-vault-gold bg-vault-gold/5' : 'border-white/10'}`}>
          <RadioGroupItem value="every" /> <span className="text-sm">Every page</span>
        </label>
        <label data-testid="split-mode-range" className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${mode === 'range' ? 'border-vault-gold bg-vault-gold/5' : 'border-white/10'}`}>
          <RadioGroupItem value="range" /> <span className="text-sm">Custom range</span>
        </label>
      </RadioGroup>
      {mode === 'range' && (
        <div>
          <Label className="text-xs text-gray-400">Page ranges (e.g. 1-3,5,8-10)</Label>
          <Input
            data-testid="split-ranges-input"
            value={ranges}
            onChange={(e) => setRanges(e.target.value)}
            placeholder="1-3,5,8-10"
            className="bg-vault-bg border-white/10 mt-1"
          />
        </div>
      )}
    </div>
  );
}

function CompressOptions({ getRef }) {
  const [level, setLevel] = React.useState('recommended');
  const [useTargetSize, setUseTargetSize] = React.useState(false);
  const [targetSize, setTargetSize] = React.useState('');
  const [targetUnit, setTargetUnit] = React.useState('MB');
  const [dpi, setDpi] = React.useState(150);
  const [removeMetadata, setRemoveMetadata] = React.useState(true);
  const [grayscale, setGrayscale] = React.useState(false);
  const [pageFrom, setPageFrom] = React.useState('');
  const [pageTo, setPageTo] = React.useState('');

  React.useImperativeHandle(getRef, () => () => ({
    level,
    targetSizeKB: useTargetSize && targetSize ? (targetUnit === 'MB' ? parseFloat(targetSize) * 1024 : parseFloat(targetSize)) : undefined,
    imageDPI: dpi,
    removeMetadata,
    grayscale,
    pageRange: pageFrom || pageTo ? { from: parseInt(pageFrom) || 1, to: parseInt(pageTo) || 99999 } : undefined
  }));

  const cards = [
    { id: 'maximum', label: 'Maximum Compression', sub: '~70–85% smaller', desc: 'Smallest file size. Reduces image resolution. Best for email & web.', chips: ['Email', 'WhatsApp'] },
    { id: 'recommended', label: 'Recommended', sub: '~40–60% smaller', desc: 'Best quality-to-size balance. Ideal for professional use.', chips: ['Office Docs', 'Archive'] },
    { id: 'low', label: 'Low Compression', sub: '~10–20% smaller', desc: 'Minimal compression, maximum quality retained.', chips: ['Print', 'Design'] }
  ];

  return (
    <div className="space-y-6">
      {/* 3 Compression Level Cards Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cards.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setLevel(c.id)}
            className={`p-4 rounded-xl border text-left transition relative flex flex-col justify-between ${
              level === c.id ? 'border-vault-gold bg-vault-gold/5 ring-1 ring-vault-gold' : 'border-white/10 hover:border-white/20 bg-vault-paper/20'
            }`}
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-white">{c.label}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-vault-gold/10 text-vault-gold font-mono">{c.sub}</span>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed mb-4">{c.desc}</p>
            </div>
            <div className="flex gap-1.5 mt-auto">
              {c.chips.map(chip => (
                <span key={chip} className="text-[10px] bg-white/5 border border-white/10 px-2 py-0.5 rounded text-gray-400">{chip}</span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {/* Advanced Options Block */}
      <div className="border border-white/10 rounded-xl bg-vault-paper/10 overflow-hidden">
        <div className="p-4 bg-white/5 border-b border-white/10 flex items-center justify-between">
          <span className="text-sm font-semibold text-white">Advanced Custom Settings</span>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/5 pb-4">
            <div>
              <Label className="text-sm text-white">Compress to specific target size</Label>
              <p className="text-xs text-gray-500">Auto-calculates optimal parameters via search loops.</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={useTargetSize} onChange={(e) => setUseTargetSize(e.target.checked)} className="accent-vault-gold" />
              {useTargetSize && (
                <div className="flex items-center bg-vault-bg border border-white/10 rounded-lg overflow-hidden h-9">
                  <input type="number" value={targetSize} onChange={(e) => setTargetSize(e.target.value)} placeholder="Size" className="w-20 px-2 bg-transparent text-sm outline-none text-white" />
                  <select value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)} className="bg-white/5 border-l border-white/10 text-xs text-gray-300 h-full px-2 outline-none">
                    <option value="MB">MB</option>
                    <option value="KB">KB</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-xs text-gray-400">Max Image DPI Cap</Label>
              <select value={dpi} onChange={(e) => setDpi(Number(e.target.value))} className="w-full bg-vault-bg border border-white/10 rounded-lg p-2 mt-1 text-white text-xs">
                <option value={72}>72 DPI (Web Optimized)</option>
                <option value={96}>96 DPI (Standard Quality)</option>
                <option value={150}>150 DPI (Recommended / Balanced)</option>
                <option value={200}>200 DPI (High Resolution)</option>
                <option value={300}>300 DPI (Print Quality)</option>
              </select>
            </div>
            <div className="flex flex-col justify-end space-y-3">
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input type="checkbox" checked={removeMetadata} onChange={(e) => setRemoveMetadata(e.target.checked)} className="accent-vault-gold" />
                Strip Document Metadata
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input type="checkbox" checked={grayscale} onChange={(e) => setGrayscale(e.target.checked)} className="accent-vault-gold" />
                Convert Document to Grayscale
              </label>
            </div>
          </div>

          <div className="pt-2 border-t border-white/5">
            <Label className="text-xs text-gray-400">Page Range Execution (Optional)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="number" value={pageFrom} onChange={(e) => setPageFrom(e.target.value)} placeholder="From" className="w-28 bg-vault-bg border border-white/10 rounded-lg p-2 text-xs text-white" />
              <span className="text-gray-500 text-xs">to</span>
              <input type="number" value={pageTo} onChange={(e) => setPageTo(e.target.value)} placeholder="To" className="w-28 bg-vault-bg border border-white/10 rounded-lg p-2 text-xs text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RotateOptions({ getRef }) {
  const [angle, setAngle] = React.useState(90);
  const [pages, setPages] = React.useState('all');
  React.useImperativeHandle(getRef, () => () => ({ angle, pages }));
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Rotation angle</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {[90, 180, 270].map((a) => (
            <button
              key={a}
              type="button"
              data-testid={`rotate-angle-${a}`}
              onClick={() => setAngle(a)}
              className={`p-3 rounded-lg border ${angle === a ? 'border-vault-gold bg-vault-gold/5 text-vault-gold' : 'border-white/10 text-gray-300'}`}
            >
              {a}°
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs text-gray-400">Pages (e.g. "all" or "1,3,5")</Label>
        <Input
          data-testid="rotate-pages-input"
          value={pages}
          onChange={(e) => setPages(e.target.value)}
          className="bg-vault-bg border-white/10 mt-1"
        />
      </div>
    </div>
  );
}

function PasswordOptions({ getRef, label = 'Set password' }) {
  const [password, setPassword] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ password }));
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">{label}</Label>
      <Input
        data-testid="password-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter at least 4 characters"
        className="bg-vault-bg border-white/10 mt-2"
      />
    </div>
  );
}

function PagesOptions({ getRef, placeholder = '1-3,5,7-9' }) {
  const [pages, setPages] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ pages }));
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Page numbers</Label>
      <Input
        data-testid="pages-input"
        value={pages}
        onChange={(e) => setPages(e.target.value)}
        placeholder={placeholder}
        className="bg-vault-bg border-white/10 mt-2"
      />
      <p className="text-xs text-gray-500 mt-1.5">Use commas and ranges (e.g. {placeholder})</p>
    </div>
  );
}

function ReorderOptions({ getRef }) {
  const [order, setOrder] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ order }));
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">New page order</Label>
      <Input
        data-testid="order-input"
        value={order}
        onChange={(e) => setOrder(e.target.value)}
        placeholder="3,1,2,4"
        className="bg-vault-bg border-white/10 mt-2"
      />
      <p className="text-xs text-gray-500 mt-1.5">Comma-separated 1-indexed pages (e.g. 3,1,2,4)</p>
    </div>
  );
}

function CropOptions({ getRef }) {
  const [m, setM] = React.useState({ top: 36, right: 36, bottom: 36, left: 36 });
  React.useImperativeHandle(getRef, () => () => ({
    margin_top: m.top, margin_right: m.right, margin_bottom: m.bottom, margin_left: m.left,
  }));
  const set = (k) => (e) => setM({ ...m, [k]: parseFloat(e.target.value) || 0 });
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Crop margins (points · 72=1in)</Label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
        {['top', 'right', 'bottom', 'left'].map((side) => (
          <div key={side}>
            <Label className="text-xs text-gray-400 capitalize">{side}</Label>
            <Input
              data-testid={`crop-${side}-input`}
              type="number"
              value={m[side]}
              onChange={set(side)}
              className="bg-vault-bg border-white/10 mt-1"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function WatermarkOptions({ getRef }) {
  const [text, setText] = React.useState('CONFIDENTIAL');
  const [opacity, setOpacity] = React.useState(0.25);
  React.useImperativeHandle(getRef, () => () => ({ text, opacity }));
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Watermark text</Label>
        <Input
          data-testid="watermark-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="bg-vault-bg border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="text-xs text-gray-400">Opacity: {Math.round(opacity * 100)}%</Label>
        <input
          data-testid="watermark-opacity"
          type="range" min="0.1" max="1" step="0.05"
          value={opacity}
          onChange={(e) => setOpacity(parseFloat(e.target.value))}
          className="w-full accent-vault-gold mt-1"
        />
      </div>
    </div>
  );
}

function PageNumberOptions({ getRef }) {
  const [position, setPosition] = React.useState('bottom-center');
  const [start, setStart] = React.useState(1);
  React.useImperativeHandle(getRef, () => () => ({ position, start }));
  const positions = [
    'top-left', 'top-center', 'top-right',
    'bottom-left', 'bottom-center', 'bottom-right',
  ];
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Position</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {positions.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`page-pos-${p}`}
              onClick={() => setPosition(p)}
              className={`p-2 rounded-lg border text-xs ${
                position === p ? 'border-vault-gold bg-vault-gold/5 text-vault-gold' : 'border-white/10 text-gray-300'
              }`}
            >
              {p.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs text-gray-400">Start from</Label>
        <Input
          data-testid="page-start-input"
          type="number"
          value={start}
          onChange={(e) => setStart(parseInt(e.target.value, 10) || 1)}
          className="bg-vault-bg border-white/10 mt-1 w-32"
        />
      </div>
    </div>
  );
}

function HeaderFooterOptions({ getRef }) {
  const [header, setHeader] = React.useState('');
  const [footer, setFooter] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ header, footer }));
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Header text</Label>
        <Input
          data-testid="header-input"
          value={header}
          onChange={(e) => setHeader(e.target.value)}
          placeholder="e.g. Company name"
          className="bg-vault-bg border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Footer text</Label>
        <Input
          data-testid="footer-input"
          value={footer}
          onChange={(e) => setFooter(e.target.value)}
          placeholder="e.g. © 2026 Your Company"
          className="bg-vault-bg border-white/10 mt-2"
        />
      </div>
    </div>
  );
}

function FindReplaceOptions({ getRef }) {
  const [find, setFind] = React.useState('');
  const [replace, setReplace] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ find, replace }));
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Find text</Label>
        <Input
          data-testid="find-input"
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder="Text to find"
          className="bg-vault-bg border-white/10 mt-2"
        />
      </div>
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Replace with</Label>
        <Input
          data-testid="replace-input"
          value={replace}
          onChange={(e) => setReplace(e.target.value)}
          placeholder="New text"
          className="bg-vault-bg border-white/10 mt-2"
        />
      </div>
    </div>
  );
}

function RedactOptions({ getRef }) {
  const [terms, setTerms] = React.useState('');
  React.useImperativeHandle(getRef, () => () => ({ terms }));
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Terms to redact (comma-separated)</Label>
      <Input
        data-testid="redact-terms-input"
        value={terms}
        onChange={(e) => setTerms(e.target.value)}
        placeholder="SSN, john@email.com, password123"
        className="bg-vault-bg border-white/10 mt-2"
      />
      <p className="text-xs text-gray-500 mt-1.5">Each occurrence will be permanently blacked out.</p>
    </div>
  );
}

function SignOptions({ getRef }) {
  const [sigFile, setSigFile] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [x, setX] = React.useState(50);
  const [y, setY] = React.useState(50);
  const [width, setWidth] = React.useState(150);
  React.useImperativeHandle(getRef, () => () => {
    if (!sigFile) return { __error: 'Please upload a signature image' };
    return { signature: sigFile, page, x, y, width };
  });
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">Signature image (PNG with transparency works best)</Label>
        <input
          data-testid="signature-file-input"
          type="file"
          accept="image/*"
          onChange={(e) => setSigFile(e.target.files?.[0] || null)}
          className="block mt-2 text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-vault-gold file:text-vault-paper file:font-semibold"
        />
        {sigFile && <p className="text-xs text-vault-gold mt-1">{sigFile.name}</p>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-gray-400">Page</Label>
          <Input data-testid="sign-page-input" type="number" value={page} onChange={(e) => setPage(parseInt(e.target.value, 10) || 1)} className="bg-vault-bg border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-gray-400">X (left, pts)</Label>
          <Input data-testid="sign-x-input" type="number" value={x} onChange={(e) => setX(parseFloat(e.target.value) || 0)} className="bg-vault-bg border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-gray-400">Y (bottom, pts)</Label>
          <Input data-testid="sign-y-input" type="number" value={y} onChange={(e) => setY(parseFloat(e.target.value) || 0)} className="bg-vault-bg border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-gray-400">Width (pts)</Label>
          <Input data-testid="sign-width-input" type="number" value={width} onChange={(e) => setWidth(parseFloat(e.target.value) || 100)} className="bg-vault-bg border-white/10 mt-1" />
        </div>
      </div>
    </div>
  );
}

function OcrOptions({ getRef }) {
  const [lang, setLang] = React.useState('eng');
  React.useImperativeHandle(getRef, () => () => ({ lang }));
  const langs = [
    { v: 'eng', label: 'English' },
    { v: 'hin', label: 'Hindi' },
    { v: 'fra', label: 'French' },
    { v: 'spa', label: 'Spanish' },
    { v: 'deu', label: 'German' },
  ];
  return (
    <div>
      <Label className="text-sm font-mono tracking-wider uppercase text-gray-400">OCR language</Label>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2">
        {langs.map((l) => (
          <button
            key={l.v}
            type="button"
            data-testid={`ocr-lang-${l.v}`}
            onClick={() => setLang(l.v)}
            className={`p-2 rounded-lg border text-sm ${
              lang === l.v ? 'border-vault-gold bg-vault-gold/5 text-vault-gold' : 'border-white/10 text-gray-300'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">Only English pre-installed. Other languages require additional tessdata packs.</p>
    </div>
  );
}

// Tool option resolver
function getToolOptionsComponent(slug) {
  switch (slug) {
    case 'split-pdf': return SplitOptions;
    case 'compress-pdf': return CompressOptions;
    case 'rotate-pdf': return RotateOptions;
    case 'protect-pdf':
    case 'unlock-pdf':
      return PasswordOptions;
    case 'delete-pages':
    case 'extract-pages':
      return PagesOptions;
    case 'reorder-pages': return ReorderOptions;
    case 'crop-pdf': return CropOptions;
    case 'add-watermark': return WatermarkOptions;
    case 'add-page-numbers': return PageNumberOptions;
    case 'add-header-footer': return HeaderFooterOptions;
    case 'edit-pdf': return FindReplaceOptions;
    case 'redact-pdf': return RedactOptions;
    case 'sign-pdf': return SignOptions;
    case 'ocr-pdf': return OcrOptions;
    default: return null;
  }
}

export default function ToolPage() {
  const { slug } = useParams();
  const tool = getTool(slug);
  const getExtraRef = React.useRef(null);

  const faqs = tool ? getFAQs(tool) : [];
  const howSteps = tool ? [
    { t: 'Upload', d: `Drop your ${tool.accept} ${tool.multiple ? 'files' : 'file'} in the secure vault.` },
    { t: 'Process', d: `Our engine handles your ${tool.name.toLowerCase()} operation in seconds.` },
    { t: 'Download', d: 'Grab the result. We delete it from our servers in 60 minutes.' },
  ] : [];

  useSEO({
    title: tool ? `${tool.name} — Free Online, No Limits | ValutDocu` : 'ValutDocu',
    description: tool ? `${tool.description}. Free unlimited use, no watermarks, no signup. Files auto-deleted in 60 min. ${tool.name} powered by ValutDocu — the iLovePDF alternative.` : '',
    keywords: tool ? `${tool.name.toLowerCase()}, ${tool.slug.replace(/-/g, ' ')}, free ${tool.name.toLowerCase()}, online ${tool.name.toLowerCase()}, ilovepdf alternative` : '',
    canonical: tool ? `${process.env.REACT_APP_BACKEND_URL}/${slug}` : undefined,
    schemas: tool && tool.available ? [buildFAQSchema(faqs), buildHowToSchema(tool.name, howSteps)] : [],
  });

  if (!tool) return <Navigate to="/" replace />;

  if (!tool.available) {
    return (
      <div data-testid="tool-coming-soon" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-vault-gold/10 border border-vault-gold/30 flex items-center justify-center mb-6">
          <ToolIcon name={tool.icon} color={tool.color} />
        </div>
        <h1 className="text-4xl font-heading font-bold tracking-tighter mb-3">{tool.name}</h1>
        <p className="text-gray-400 mb-6">{tool.description}</p>
        <div className="inline-block px-4 py-2 rounded-full bg-vault-gold/10 border border-vault-gold/30 text-vault-gold text-sm font-mono tracking-wider">
          COMING SOON
        </div>
        <div className="mt-10">
          <Link to="/" className="btn-ghost inline-flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Back to all tools
          </Link>
        </div>
      </div>
    );
  }

  const OptionsComp = getToolOptionsComponent(slug);
  const related = getRelatedTools(slug, 5);

  return (
    <div data-testid="tool-page" className="vault-grid-bg min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-12 pb-16">
        <Link to="/" data-testid="back-to-home" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-vault-gold mb-8 transition">
          <ChevronLeft className="w-4 h-4" /> All tools
        </Link>

        <div className="flex items-start gap-5 mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${tool.color}15`, border: `1px solid ${tool.color}30` }}
          >
            <ToolIcon name={tool.icon} color={tool.color} size={36} />
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold">{tool.category}</p>
            <h1 data-testid="tool-title" className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tighter mt-1">
              {tool.name} <span className="text-gray-500">—</span> <span className="gold-text">Free. No Limits.</span>
            </h1>
            <p className="text-gray-400 mt-3 max-w-2xl">{tool.description}. Process unlimited files, no watermarks, no signup required.</p>
          </div>
        </div>

        <ToolUploader
          tool={tool}
          extraFields={OptionsComp ? <OptionsComp getRef={getExtraRef} /> : null}
          getExtraFormData={OptionsComp ? () => (getExtraRef.current ? getExtraRef.current() : null) : null}
        />

        {/* Trust row */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          {[
            { Icon: Clock, text: '60 min auto-delete' },
            { Icon: Lock, text: '256-bit SSL' },
            { Icon: ShieldCheck, text: 'No watermarks' },
            { Icon: Sparkles, text: 'Unlimited free' },
          ].map(({ Icon, text }, i) => (
            <div key={i} className="bg-vault-paper/40 border border-white/10 rounded-lg p-3 flex items-center justify-center gap-2">
              <Icon className="w-4 h-4 text-vault-gold" />
              <span className="text-xs text-gray-300">{text}</span>
            </div>
          ))}
        </div>

        {/* How it works */}
        <section data-testid="how-it-works-tool" className="mt-16">
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6">How to {tool.name.toLowerCase()}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {howSteps.map(({ t, d }, i) => {
              const Icons = [Upload, Cog, Download];
              const Icon = Icons[i] || Upload;
              return (
                <div key={i} className="tool-card rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-5 h-5 text-vault-gold" />
                    <span className="font-mono text-xs tracking-[0.2em] text-vault-gold">0{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-white">{t}</h3>
                  <p className="text-sm text-gray-400 mt-1 leading-relaxed">{d}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Ad slot below how-it-works */}
        <AdSlot slot="tool-mid" testId="ad-tool-mid" className="my-12" />

        {/* FAQ */}
        {faqs.length > 0 && (
          <section data-testid="faq-section" className="mt-16">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="bg-vault-paper/40 border border-white/10 rounded-xl">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} data-testid={`faq-${i}`} className="border-white/10 px-5">
                  <AccordionTrigger className="text-white text-left hover:text-vault-gold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Related tools */}
        {related.length > 0 && (
          <section data-testid="related-tools" className="mt-16">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6">Related tools</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="tool-card rounded-lg p-4 text-center"
                >
                  <div className="mb-2 flex justify-center">
                    <ToolIcon name={r.icon} color={r.color} size={28} />
                  </div>
                  <div className="text-xs font-semibold text-white">{r.name}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
